<template>
  <div class="top-header-container">
    <div class="header-actions">
      <!-- Notification Dropdown -->
      <div class="notification-dropdown" v-click-outside="closeNotifications">
        <button class="notif-bell-btn" @click="toggleNotifications">
          <i class="fas fa-bell"></i>
          <span v-if="notifications.length > 0" class="notif-badge">{{ notifications.length }}</span>
        </button>
        
        <div v-show="showNotifications" class="notif-menu">
          <div class="notif-header">
            <strong>Notifications</strong>
            <button class="clear-notifs-btn" v-if="notifications.length > 0" @click="clearNotifications">Clear</button>
          </div>
          <div class="notif-body">
            <div v-if="notifications.length === 0" class="notif-empty">
              No new notifications
            </div>
            <div v-else>
              <div 
                v-for="(notif, idx) in notifications" 
                :key="idx" 
                class="notif-item"
                @click="handleNotificationClick(notif)"
              >
                <div class="notif-icon">{{ notif.type === 'contract_signed' ? '✍️' : '🎉' }}</div>
                <div class="notif-content">
                  <strong>{{ notif.orgName }}</strong> 
                  <span v-if="notif.type === 'contract_signed'"> just signed and executed a contract!</span>
                  <span v-else> just accepted a proposal securely!</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Product Switcher with Collapse/Expand -->
      <div 
        class="product-switcher-wrapper" 
        v-if="products && products.length > 0" 
        v-click-outside="collapseProducts"
      >
        <!-- Toggle button to expand/collapse (only if there are multiple products) -->
        <button 
          v-if="otherProducts.length > 0" 
          class="switcher-toggle-btn" 
          @click="toggleProductSwitcher"
          :title="isProductsExpanded ? 'Collapse products' : 'Show all products'"
        >
          <i class="fas" :class="isProductsExpanded ? 'fa-chevron-right' : 'fa-chevron-left'"></i>
        </button>

        <!-- Collapsible list of other products -->
        <div class="collapsible-products" :class="{ 'expanded': isProductsExpanded }">
          <a 
            v-for="(product, index) in otherProducts" 
            :key="index" 
            :href="product.link" 
            target="_blank" 
            :title="'Switch to ' + product.name"
            class="product-icon-btn"
          >
            <img v-if="product.logo" :src="product.logo" :alt="product.name" class="img-fluid" />
            <span v-else class="logo-fallback">{{ product.name?.substring(0, 1) }}</span>
          </a>
        </div>

        <!-- Default product (always visible) -->
        <a 
          v-if="defaultProduct"
          :href="defaultProduct.link" 
          target="_blank" 
          :title="'Switch to ' + defaultProduct.name"
          :class="{ 'default-product': defaultProduct.isDefault }"
          class="product-icon-btn main-default-product"
        >
          <img v-if="defaultProduct.logo" :src="defaultProduct.logo" :alt="defaultProduct.name" class="img-fluid" />
          <span v-else class="logo-fallback">{{ defaultProduct.name?.substring(0, 1) }}</span>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      products: [],
      isProductsExpanded: false,
      showNotifications: false,
      notifications: [],
      wsSocket: null
    };
  },
  computed: {
    isAuthenticated() {
      // Check if store exists and token is present
      return !!(this.$store && this.$store.state && this.$store.state.auth && this.$store.state.auth.accessToken);
    },
    currentUserId() {
      return this.$store?.state?.auth?.jwtPayload?.user_id || 
             this.$store?.state?.auth?.currentUser?.id || 
             'default';
    },
    notificationsStorageKey() {
      return `vertex_notifications_${this.currentUserId}`;
    },
    defaultProduct() {
      if (!this.products || this.products.length === 0) return null;
      return this.products.find(p => p.isDefault) || this.products[0];
    },
    otherProducts() {
      if (!this.products || this.products.length <= 1) return [];
      const def = this.defaultProduct;
      return this.products.filter(p => p !== def);
    }
  },
  watch: {
    isAuthenticated: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal && !oldVal) {
          this.loadNotifications();
          this.connectWebSocket();
        } else if (!newVal && oldVal && this.wsSocket) {
          this.wsSocket.close();
          this.wsSocket = null;
        }
      }
    },
    '$store.state.auth.accessToken'(newToken, oldToken) {
      if (newToken !== oldToken && this.isAuthenticated) {
        if (this.wsSocket) {
          this.wsSocket.close();
          this.wsSocket = null;
        }
        this.loadNotifications();
        this.connectWebSocket();
      }
    },
    currentUserId(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.loadNotifications();
      }
    },
    '$store.state.auth.currentOrganisation.id'(newId, oldId) {
      if (newId && newId !== oldId) {
        this.getProducts();
      }
    }
  },
  mounted() {
    // Only fetch if authenticated and organization is set
    if (this.$store && this.$store.state && this.$store.state.auth && this.$store.state.auth.isAuthenticated) {
      this.getProducts();
    }
    
    // Load persisted notifications for current user
    this.loadNotifications();
  },
  methods: {
    loadNotifications() {
      const saved = localStorage.getItem(this.notificationsStorageKey);
      if (saved) {
        try {
          this.notifications = JSON.parse(saved);
        } catch(e) {
          console.warn("Could not load persisted notifications", e);
          this.notifications = [];
        }
      } else {
        this.notifications = [];
      }
    },
    toggleNotifications() {
      this.showNotifications = !this.showNotifications;
    },
    closeNotifications() {
      this.showNotifications = false;
    },
    clearNotifications() {
      this.notifications = [];
      this.saveNotifications();
    },
    saveNotifications() {
      localStorage.setItem(this.notificationsStorageKey, JSON.stringify(this.notifications));
    },
    handleNotificationClick(notif) {
      this.showNotifications = false;
      if (notif.type === 'contract_signed') {
        if (notif.contractId) {
          this.$router.push(`/contracts?search=${encodeURIComponent(notif.contractId)}`);
        } else {
          this.$router.push(`/contracts`);
        }
      } else {
        if (notif.proposalId) {
          this.$router.push(`/proposals/${notif.proposalId}`);
        } else {
          this.$router.push(`/proposals`);
        }
      }
    },
    connectWebSocket() {
      if (this.wsSocket) return; // Prevent multiple connections
      
      const baseUrl = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8080';
      const token = this.$store?.state?.auth?.accessToken || '';
      const wsUrl = baseUrl.replace(/^http/, 'ws') + '/ws' + (token ? `?token=${encodeURIComponent(token)}` : '');
      
      this.wsSocket = new WebSocket(wsUrl);
      
      this.wsSocket.onopen = () => {
        console.log("WebSocket connected for dashboard notifications");
      };
      
      this.wsSocket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data && data.event) {
            if (data.event.startsWith("proposal_accepted|")) {
              const parts = data.event.split("|");
              const orgName = parts.length > 1 ? parts[1] : "A client";
              const proposalId = parts.length > 2 ? parts[2] : null;
              
              this.notifications.unshift({ 
                type: 'proposal_accepted',
                orgName, 
                proposalId, 
                timestamp: new Date().getTime() 
              });
              this.saveNotifications();
              
              // Still show a toast, but this time with a link
              this.$toast.success(`🎉 Proposal accepted by ${orgName}! Click to view.`, {
                timeout: 10000,
                onClick: () => {
                  if (proposalId) {
                    this.$router.push(`/proposals/${proposalId}`);
                  }
                }
              });
            } else if (data.event.startsWith("contract_signed|")) {
              const parts = data.event.split("|");
              const orgName = parts.length > 1 ? parts[1] : "A client";
              const contractId = parts.length > 2 ? parts[2] : null;
              
              this.notifications.unshift({ 
                type: 'contract_signed',
                orgName, 
                contractId, 
                timestamp: new Date().getTime() 
              });
              this.saveNotifications();
              
              this.$toast.success(`✍️ Contract signed by ${orgName}! Click to view.`, {
                timeout: 10000,
                onClick: () => {
                  if (contractId) {
                    this.$router.push(`/contracts?search=${encodeURIComponent(contractId)}`);
                  } else {
                    this.$router.push(`/contracts`);
                  }
                }
              });
            }
          }
        } catch (e) {
          console.error("WebSocket parsing error:", e);
        }
      };
      
      this.wsSocket.onclose = () => {
        this.wsSocket = null;
        // Simple reconnect logic if still authenticated
        if (this.isAuthenticated) {
          setTimeout(() => {
            this.connectWebSocket();
          }, 5000);
        }
      };
    },
    async getProducts() {
      try {
        const payload = this.$store.state.auth.jwtPayload;
        const roles = payload ? [
          ...(Array.isArray(payload.roles) ? payload.roles : (payload.roles ? [payload.roles] : [])),
          ...(Array.isArray(payload.role) ? payload.role : (payload.role ? [payload.role] : [])),
          ...(Array.isArray(payload.vertex_roles) ? payload.vertex_roles : (payload.vertex_roles ? [payload.vertex_roles] : [])),
        ] : [];
        const isTrueSuperAdmin = this.$store.state.auth.isSuperAdmin ||
          roles.some(r => typeof r === 'string' && (r.toLowerCase().includes('super') || r.toLowerCase().replace(/[\s_-]+/g, '') === 'superadmin'));
        const isOrgAdmin = this.$store.state.auth.isOrgAdmin;
        const orgId = this.$store.state.auth.currentOrganisation?.id || this.$store.state.auth.currentUser?.organisation?.id;
        let url;

        if (isTrueSuperAdmin) {
          url = `/supadmin/products`;
        } else if (isOrgAdmin && orgId) {
          url = `/orgadmin/organisations/${orgId}/product-toolbar`;
        } else {
          const userId = payload?.user_id || this.$store.state.auth.currentUser?.id;
          if (!userId) return;
          url = `/users/${userId}/product-toolbar`;
        }
        const response = await this.$axios_auth.get(url, { _skipAuthRedirect: true });
        if (response.data.success) {
          if (isTrueSuperAdmin && response.data.products) {
            const mapped = response.data.products
              .filter(p => p.is_vertex_platform)
              .map(p => ({
                application: p.application,
                login_url: p.login_url,
                logo: p.product_logo ? p.product_logo.logo : null,
                is_default: false
              }));
            this.getProductsList(mapped);
          } else if (response.data.toolbar_products) {
            this.getProductsList(response.data.toolbar_products);
          }
        }
      } catch (error) {
        console.warn("Could not load Vertex products for switcher.", error);
      }
    },
    getProductsList(data) {
      let excludedAppNames = ["sophos", "spark"];
      this.products = [];
      var applications = [];
      
      const payload = this.$store.state.auth.jwtPayload;
      const roles = payload ? [
        ...(Array.isArray(payload.roles) ? payload.roles : (payload.roles ? [payload.roles] : [])),
        ...(Array.isArray(payload.role) ? payload.role : (payload.role ? [payload.role] : [])),
        ...(Array.isArray(payload.vertex_roles) ? payload.vertex_roles : (payload.vertex_roles ? [payload.vertex_roles] : [])),
      ] : [];
      const isTrueSuperAdmin = this.$store.state.auth.isSuperAdmin ||
        roles.some(r => typeof r === 'string' && (r.toLowerCase().includes('super') || r.toLowerCase().replace(/[\s_-]+/g, '') === 'superadmin'));
      const defaultOrgId = process.env.VUE_APP_SUPER_ADMIN_ORG_ID || 2;
      const orgSelected = isTrueSuperAdmin ? defaultOrgId : (this.$store.state.auth.currentOrganisation?.id || defaultOrgId);

      for (const p of data) {
        var name = p.application.toLowerCase();
        var url = `${p.login_url}?access_token=${this.$store.state.auth.accessToken}&org_selected=${orgSelected}`;

        if (!excludedAppNames.includes(name) && !applications.includes(name)) {
          this.products.push({ logo: p.logo, name: p.application, link: url, isDefault: !!p.is_default });
        }
        applications.push(name);
      }
    },
    toggleProductSwitcher() {
      this.isProductsExpanded = !this.isProductsExpanded;
    },
    collapseProducts() {
      this.isProductsExpanded = false;
    }
  }
};
</script>

<style scoped>
.top-header-container {
  display: flex;
  justify-content: flex-end; 
  align-items: center;
  height: 60px; 
  width: 100%;
  background-color: #ffffff; 
  padding: 0 24px;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  
  /* The native Bootstrap sticky header setup */
  position: sticky;
  top: 0;
  z-index: 1030; 
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 20px;
}

/* Notification Bell Styles */
.notification-dropdown {
  position: relative;
  display: flex;
  align-items: center;
}

.notif-bell-btn {
  background: none;
  border: none;
  color: #00A0C1;
  font-size: 20px;
  position: relative;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s, color 0.2s;
}

.notif-bell-btn:hover {
  transform: scale(1.1);
  color: #008ba8;
}

.notif-badge {
  position: absolute;
  top: 0px;
  right: 0px;
  background-color: #ff3b30;
  color: white;
  font-size: 11px;
  font-weight: bold;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #ffffff;
}

.notif-menu {
  position: absolute;
  top: 50px;
  right: -10px;
  width: 320px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  border: 1px solid #eaeaea;
  overflow: hidden;
  z-index: 1000;
  animation: fadeIn 0.2s ease;
}

.notif-header {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f9f9f9;
  color: #333;
}

.clear-notifs-btn {
  background: none;
  border: none;
  color: #00A0C1;
  font-size: 12px;
  cursor: pointer;
  padding: 0;
}
.clear-notifs-btn:hover {
  text-decoration: underline;
}

.notif-body {
  max-height: 300px;
  overflow-y: auto;
}

.notif-empty {
  padding: 24px;
  text-align: center;
  color: #888;
  font-size: 14px;
}

.notif-item {
  display: flex;
  padding: 14px 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background-color 0.2s;
}
.notif-item:last-child {
  border-bottom: none;
}
.notif-item:hover {
  background-color: #f5fafd;
}

.notif-icon {
  font-size: 20px;
  margin-right: 12px;
  display: flex;
  align-items: center;
}

.notif-content {
  font-size: 13px;
  line-height: 1.4;
  color: #444;
}
.notif-content strong {
  color: #111;
}

/* Product Switcher Styles */
.product-switcher-wrapper {
  display: flex;
  align-items: center;
}

.switcher-toggle-btn {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #64748b;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  cursor: pointer;
  margin-right: 8px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0;
  flex-shrink: 0;
}

.switcher-toggle-btn:hover {
  background: #00A0C1;
  color: #ffffff;
  border-color: #00A0C1;
  transform: scale(1.08);
}

.collapsible-products {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 0;
  opacity: 0;
  overflow: hidden;
  transition: max-width 0.35s cubic-bezier(0.4, 0, 0.2, 1),
              opacity 0.25s ease,
              margin 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  margin-right: 0;
  white-space: nowrap;
}

.collapsible-products.expanded {
  max-width: 500px;
  opacity: 1;
  margin-right: 8px;
}

.product-icon-btn {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 50%;
  border: 1px solid #e2e8f0;
  background-color: #fff; 
  overflow: hidden;
  transition: all 0.2s ease;
  cursor: pointer;
  flex-shrink: 0;
}

.product-icon-btn:hover {
  transform: scale(1.1); /* Pop out effect */
  border-color: #00A0C1;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.product-icon-btn img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  padding: 4px; /* Padding so the logo doesn't touch the edges */
}

.product-icon-btn.default-product {
  border-color: #f3994d;
}

.product-icon-btn.default-product:hover {
  border-color: #f3994d;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
}

.logo-fallback {
  width: 100%;
  height: 100%;
  background-color: #333;
  color: #fff;
  font-size: 14px;
  font-weight: bold;
  display: flex;
  justify-content: center;
  align-items: center;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>